import { EvaluationResult } from '@sharefile/entitlements-plugin-sdk';
import { SolutionCatalogType } from '@sharefile/packaging-types';
import {
	ImageInfo,
	InstallationInfo,
	SolutionInfo,
	SolutionInstallation,
} from '@sharefiledev/rubicon-pilet-sdk';

export type GetSolutionParams = {
	id: string;
};

export type DeleteSolutionParams = {
	id: string;
};

export type ListSolutionsParamsLegacy = {
	parentRID?: string;
	type?: string;
	parentRIDPrefix?: string;
};

export type CreateExecutionParams = {
	solutionId: string;
	/**
	 * Container under which the resulting resource should get created (generally the account RID)
	 */
	parentRID: string;
	args?: object;
	uninstall?: boolean;
};

export type GetExecutionParams = {
	executionId: string;
};

export type SolutionsSdkConfiguration = {
	baseUrl: string;
};

/**
 * A solution information item.
 * @interface SolutionInfoLegacy
 */
export interface SolutionInfoLegacy {
	id: string;
	version: string;
	containerRID: string;
	rootType: string;
	created: string;
	creator: string;
	modified: string;
	modifier: string;
	namespace: string;
	name: string;
	label: string;
	description?: string;
	publicationStatus: any;
	installationInfo?: InstallationInfo;
	latestVersion?: string;
}

export type SolutionInfoCompatible = SolutionInfoLegacy | SolutionInfo;

/**
 * A solution item. including definition
 * @export
 * @interface Solution
 */
export type Solution = {
	id: string;
	version: string;
	containerRID: string;
	definition: any;
	hero?: ImageInfo;
	rootType?: string;
	created: string;
	creator: string;
	modified?: string;
	modifier?: string;
	namespace?: string;
	name?: string;
	publicationStatus: any;
};

export type SolutionCategoryOption = {
	value: string;
	label: string;
};

export type Execution = {
	id: string;
	packageId: string;
	version: string;
	containerRID: string;
	created: string;
	creator: string;
	expires: number;
	status: ExecutionStatus;
	updated: string;
};

export enum ExecutionStatus {
	Requested = 'requested',
	Running = 'running',
	Success = 'success',
	Failed = 'failed',
	Rollback_Processing = 'rollback-processing',
	Rollback_Failed = 'rollback-failed',
	Rollback_Succeeded = 'rollback-succeeded',
	Rollback_Requested = 'rollback-requested',
}

export type CreateExecutionResponse = Pick<Execution, 'id'>;

// v2 types

export type ListSolutionsParams = {
	containerRID?: string;
	namespace?: string;
};

export type InstallSolutionParams = {
	solutionId: string;
	parentRID: string;
	args?: any;
	createEntitlement?: boolean;
};

export type RequestInstallSolutionArgs = {
	comments?: string;
	sendCopyToSender?: boolean;
};

export type RequestInstallSolutionParams = {
	solutionRID: string;
	args?: RequestInstallSolutionArgs;
};

export type UpdateSolutionParams = {
	solutionId: string;
	parentRID: string;
	args?: any;
};

export type UninstallSolutionParams = {
	solutionId: string;
	containerRID: string;
	deletionPolicy: DeletionPolicy;
};

export enum DeletionPolicy {
	Delete = 'Delete',
	Retain = 'Retain',
}

export type InstallSolutionResponse =
	| {
			id: string;
	  }
	| EvaluationResult;

export type UpdateSolutionResponse =
	| {
			id: string;
	  }
	| EvaluationResult;

export type UninstallSolutionResponse = {
	id: string;
};

export type GetInstallationParams = {
	id: string;
};

enum AdditionalInstallationStatus {
	AwaitingUpload = 'awaiting-upload',
	Uninstall_Requested = 'uninstall-requested',
	Uninstall_Running = 'uninstall-running',
	Uninstall_Success = 'uninstall-success',
	Uninstall_Failed = 'uninstall-failed',
}

export type InstallationStatus = ExecutionStatus | AdditionalInstallationStatus;

export type ErrorResponse = {
	message: string;
	stack?: any;
	packageExecutionId?: string;
	details?: any;
};

export type GetInstallationResponse = {
	id: string;
	solutionId: string;
	containerRID: string;
	payload: object;
	status: InstallationStatus;
	error?: Record<string, any> | ErrorResponse;
	created: string;
	creator: string;
	expires?: number;
	states?: any;
	executionIds?: string[];
	upgradeStatus?: string;
};

export type GetInstallationLogsParams = {
	executionId: string;
};

export type RunLog = {
	entry: {
		level: string;
		message: string;
		meta?: {
			[name: string]: any;
		};
		userMeta?: {
			ref: string;
			type: string;
			error?: any;
		};
	};
	timestamp: number;
};

export type GetInstallationLogsResponse = RunLog[];

export type GetInstallationEntitlementParams = {
	catalogType: SolutionCatalogType;
};

export type GetInstallationEntitlementResponse = EvaluationResult;

export type CanGetSolutionInstallationsParams = { solutionId: string };
export type CanGetSolutionInstallationsResponse = null;

export type GetSolutionInstallationsParams = CanGetSolutionInstallationsParams & {
	limit?: number;
	continuationToken?: string;
};
export type GetSolutionInstallationsResponse = {
	installations: SolutionInstallation[];
	continuationToken?: string;
};

export interface RegistryInfo {
	RID: string;
	name: string;
}

export type RegistriesListResponse = RegistryInfo[];
